// /home/labdoo/zitech-nexus/frontend/src/pages/Settings.tsx
import React, { useState } from 'react';
import './Settings.css';

// SVG Icons
const Icons = {
  Bell: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  Sms: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Moon: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  ),
  Globe: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Lock: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  Shield: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  ),
  Download: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  Trash: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M20 6L9 17L4 12" />
    </svg>
  ),
  Spinner: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="st-spinner-small">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
};

const Settings: React.FC = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    darkMode: false,
    language: 'english',
  });

  const handleToggle = (setting: string) => {
    setSettings({
      ...settings,
      [setting]: !settings[setting as keyof typeof settings],
    });
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSettings({
      ...settings,
      language: e.target.value,
    });
  };

  return (
    <div className="st-container">
      {/* Header */}
      <div className="st-header">
        <h1 className="st-title">Settings</h1>
        <p className="st-subtitle">Manage your application preferences</p>
      </div>

      <div className="st-sections">
        {/* Notifications Section */}
        <div className="st-card">
          <h2 className="st-card-title">Notifications</h2>
          
          <div className="st-item">
            <div className="st-item-left">
              <div className="st-item-icon st-icon-bell">
                <Icons.Bell />
              </div>
              <div className="st-item-info">
                <h3>Email Notifications</h3>
                <p>Receive updates about your service requests via email</p>
              </div>
            </div>
            <label className="st-toggle">
              <input
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={() => handleToggle('emailNotifications')}
              />
              <span className="st-toggle-slider"></span>
            </label>
          </div>

          <div className="st-item">
            <div className="st-item-left">
              <div className="st-item-icon st-icon-sms">
                <Icons.Sms />
              </div>
              <div className="st-item-info">
                <h3>SMS Notifications</h3>
                <p>Receive text message updates about your service requests</p>
              </div>
            </div>
            <label className="st-toggle">
              <input
                type="checkbox"
                checked={settings.smsNotifications}
                onChange={() => handleToggle('smsNotifications')}
              />
              <span className="st-toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Appearance Section */}
        <div className="st-card">
          <h2 className="st-card-title">Appearance</h2>
          
          <div className="st-item">
            <div className="st-item-left">
              <div className="st-item-icon st-icon-moon">
                <Icons.Moon />
              </div>
              <div className="st-item-info">
                <h3>Dark Mode</h3>
                <p>Switch between light and dark theme</p>
              </div>
            </div>
            <label className="st-toggle">
              <input
                type="checkbox"
                checked={settings.darkMode}
                onChange={() => handleToggle('darkMode')}
              />
              <span className="st-toggle-slider"></span>
            </label>
          </div>

          <div className="st-item">
            <div className="st-item-left">
              <div className="st-item-icon st-icon-globe">
                <Icons.Globe />
              </div>
              <div className="st-item-info">
                <h3>Language</h3>
                <p>Select your preferred language</p>
              </div>
            </div>
            <select value={settings.language} onChange={handleLanguageChange} className="st-select">
              <option value="english">English</option>
              <option value="spanish">Spanish</option>
              <option value="french">French</option>
              <option value="german">German</option>
            </select>
          </div>
        </div>

        {/* Security Section */}
        <div className="st-card">
          <h2 className="st-card-title">Security</h2>
          
          <div className="st-item">
            <div className="st-item-left">
              <div className="st-item-icon st-icon-lock">
                <Icons.Lock />
              </div>
              <div className="st-item-info">
                <h3>Change Password</h3>
                <p>Update your account password</p>
              </div>
            </div>
            <button className="st-btn st-btn-primary">Change</button>
          </div>

          <div className="st-item">
            <div className="st-item-left">
              <div className="st-item-icon st-icon-shield">
                <Icons.Shield />
              </div>
              <div className="st-item-info">
                <h3>Two-Factor Authentication</h3>
                <p>Add an extra layer of security to your account</p>
              </div>
            </div>
            <button className="st-btn st-btn-primary">Enable</button>
          </div>
        </div>

        {/* Data Management Section */}
        <div className="st-card">
          <h2 className="st-card-title">Data Management</h2>
          
          <div className="st-item">
            <div className="st-item-left">
              <div className="st-item-icon st-icon-download">
                <Icons.Download />
              </div>
              <div className="st-item-info">
                <h3>Export Data</h3>
                <p>Download all your data from ZiTech Nexus</p>
              </div>
            </div>
            <button className="st-btn st-btn-primary">Export</button>
          </div>

          <div className="st-item st-item-danger">
            <div className="st-item-left">
              <div className="st-item-icon st-icon-trash">
                <Icons.Trash />
              </div>
              <div className="st-item-info">
                <h3>Delete Account</h3>
                <p>Permanently delete your account and all data</p>
              </div>
            </div>
            <button className="st-btn st-btn-danger">Delete Account</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;